import type { Result } from '@de/types/result';
import { isAccreditationHidden } from '../accreditationVisibility';

/**
 * Polymorphic `location.state` as observed on the Offerings `/match`
 * card projection: usually a string (`"AL"`), but occasionally an
 * object (e.g. University of Fredericton →
 * `{ id, name, abbreviation }`). The normalizer collapses it to a
 * display string so a card bind never renders `[object Object]`
 * (match-integration handoff, open gap #2).
 */
export type OfferingMatchState =
  | string
  | {
      id?: number;
      name?: string;
      abbreviation?: string;
    };

/**
 * The per-org **card projection** Offerings attaches to each `/match`
 * item. In practice `organizations` is a single-element array; this is
 * `organizations[0]`. Every field is optional — dev orgs whose cache is
 * unseeded arrive without it ("missing-org cards").
 */
export interface OfferingMatchOrg {
  name?: string;
  slug?: string;
  images?: {
    logo?: string;
    hero?: string;
  };
  location?: {
    city?: string;
    state?: OfferingMatchState;
  };
  externalIds?: {
    /** Legacy EduDirect school id — maps to `Result.schoolId`. */
    edudirectId?: string;
    /** School DB UUID. */
    schoolId?: string;
  };
  tags?: {
    isOnline?: boolean;
    isNotForProfit?: boolean;
    isPrivate?: boolean;
    isPublic?: boolean;
    isForProfit?: boolean;
  };
  levelOfInstitution?: string;
  accreditator?: string;
  percentOnlineEnrollment?: number | string | null;
  /** Org marketing `formCustomizations.showActiveProspectScript`. */
  showActiveProspectScript?: boolean;
  /**
   * Non-promoted institutional stats keyed by metric slug, each a
   * `{ label, value }` pair (offerings `OrgDisplayDataDto.institutionalStats`).
   * `value` is polymorphic (number / boolean / pre-formatted string). The
   * normalizer maps a subset of slugs onto discrete `Result` fields and
   * also exposes the whole map — merged with synthesized entries for the
   * label-less org tag signals (see {@link mergeTagSignalsIntoStats}) —
   * as `Result.institutionalStats` for DSL-side derivations (e.g. the
   * `tagLabels` bind transform).
   */
  institutionalStats?: Record<string, { label?: string; value?: unknown }>;
}

/**
 * A single Offerings `/match` result item (`SearchOfferingResultDto`),
 * narrowed to the fields the card + lead-flow handoff consume. Kept
 * permissive (`unknown`-tolerant) because the normalizer is the
 * defensive boundary between the raw service payload and the typed
 * `Result` contract.
 */
export interface OfferingMatchItem {
  /** Canonical Offerings offering identity (UUID). */
  offeringUuid?: string;
  /**
   * Organizations-service UUID(s) the offering belongs to
   * (`SearchDisplayOfferingResponseDto.organizationIds`). An offering can
   * belong to multiple organizations, so this is an array; the normalizer
   * stamps the first/primary entry onto `Result.organizationId` for the v4
   * LDS submit path.
   */
  organizationIds?: ReadonlyArray<string>;
  /** Program/offering display name. */
  name?: string;
  /** Legacy numeric program id, surfaced as a string by the service. */
  externalId?: string | number;
  shortDescription?: string;
  /**
   * Per-offering dynamic links the `/match` projection surfaces. Each carries a
   * discriminating `type` (e.g. `application_cta`, `linkout`) plus a base
   * `url`. The normalizer reads the `type === 'linkout'` entry's `url` into
   * `Result.linkoutUrl` so the result card knows the offering **has** a linkout
   * even though the conversion-interpolated (`heclid`-bearing) URL is resolved
   * by CMS at click time, not here.
   */
  dynamicLinks?: ReadonlyArray<{ url?: string; type?: string }>;
  /**
   * Server-resolved application CTA the Offerings `/match` projection
   * surfaces per result (EDUN-12673). `url` is fully resolved/interpolated
   * from the offering's `application_cta` dynamic link except the runtime
   * tokens (`clickId`, `correlationId`), which Offerings leaves empty;
   * `label` is nullable. Present only when the offering has an
   * `application_cta` dynamic link, so the field is optional/nullable and
   * the normalizer falls back to empty strings when it is absent.
   */
  applicationCta?: {
    url?: string;
    label?: string | null;
    /**
     * Click-time interpolation config (EDUN-14479): Offerings resolves
     * offering-level vs. the primary organization's cached `attributes.
     * applicationCta` (whole-object fallback) before responding, so the
     * normalizer only needs to carry whichever one comes back.
     */
    staticParams?: Record<string, string>;
    fieldMap?: Record<string, string>;
    missingValue?: 'empty' | 'omit' | 'hide';
  } | null;
  postLeadCta?: {
    url?: string;
    label?: string | null;
    staticParams?: Record<string, string>;
    fieldMap?: Record<string, string>;
    missingValue?: 'empty' | 'omit' | 'hide';
  } | null;
  synchronousLeadDelivery?: boolean;
  organizations?: ReadonlyArray<OfferingMatchOrg>;
  /**
   * Open-ended operational attributes from Offerings. The normalizer retains
   * the plain-object bag for result-card bindings and separately promotes the
   * `programFields` pointer used by lead qualification.
   */
  attributes?: Record<string, unknown> | null;
  /**
   * Flat `/v1/offerings/search` only — set by the service when the request
   * opts into related offerings (`includeRelated: true`). The response
   * coercer partitions on this flag to move related rows into a
   * `related: { data: [...] }` bucket the runtime can iterate separately;
   * the flag itself is not carried onto the normalized `Result`.
   */
  related?: boolean;
}

/**
 * **Interim Configurable Experiences normalizer** — reshapes one raw
 * Offerings `/match` item into the legacy `Result` contract so the
 * existing result-card render, governed lead flow, dedupe registry, and
 * offering detail-by-id drawer all consume a single, stable object.
 *
 * Why this exists (and why it's interim): the `/match` payload is
 * UUID-native and nests school data under `organizations[]`, while the
 * ~60-file `Result` contract expects flat, legacy numeric/string ids.
 * Rather than rewrite that contract or push heavy normalization into the
 * DSM binding grammar (which has no array indexing), this maps once, at
 * the response boundary. The durable fix is
 * service-/BFF-side normalization; retire this when that lands.
 *
 * Field policy:
 *   - **Forward identity** — `offeringUuid` is carried verbatim; it
 *     drives `{{item.offeringUuid}}` → `formState.selectedOfferingId`.
 *   - **v4 identity** — `organizationId` (the Organizations-service UUID
 *     the v4 LDS submit path routes on, sourced from
 *     `organizationIds[0]`) and `offeringId` (the CMS v2 conversion id,
 *     sourced from `offeringUuid` — the offerings UUID is the canonical
 *     offering identity, with no distinct CMS-side id) are stamped so the
 *     v4 lead submit + post-result-question and CMS v2 conversion guards
 *     resolve. Both are left absent when the `/match` item lacks them, so
 *     a missing-org card still renders (it is simply not v4-submittable).
 *   - **Legacy identity (best-effort)** — `schoolId`
 *     (`organizations[0].externalIds.edudirectId`), `schoolSlug`,
 *     `schoolName`, and numeric `programId` (`Number(externalId)`) are
 *     filled so the still-live v3 submit + duplicate gate keep working
 *     until the v4 UUID-native path lands. `programId`
 *     defaults to `0` when `externalId` is absent/non-numeric — a
 *     non-submittable sentinel that keeps `programId.toString()` and the
 *     dedupe registry safe.
 *   - **Display fields** — `schoolName`, `city`, `state` (de-polymorphed),
 *     `imageURL`, institution stats — sourced from the card projection
 *     with empty/`false`/`null` fallbacks so a sparse/missing org renders
 *     blank rather than crashing.
 *   - **Drawer-detail fields** — `tcpa`, `valueProps`, `programFlow`,
 *     `spotlight`, … are intentionally left empty here; the offering
 *     detail-by-id source fills them via
 *     `mapOfferingDetailToResultDetailFields`.
 *
 * Total and defensive: never throws; always returns a renderable
 * `Result`.
 */
export function normalizeOfferingsMatchItem(
  item: OfferingMatchItem,
  resultsSource?: string,
  listId?: string,
): Result {
  // Stay total: the response coercer maps over `unknown` service items with a
  // cast, so a malformed entry (e.g. `null` or a primitive) must not throw and
  // turn the whole `/match` source into an error. Fall back to an empty org.
  const safeItem: OfferingMatchItem =
    typeof item === 'object' && item !== null ? item : {};
  const org = safeItem.organizations?.[0];

  const result: Result = {
    schoolName: org?.name ?? '',
    schoolId: org?.externalIds?.edudirectId ?? '',
    schoolSlug: org?.slug ?? '',
    city: org?.location?.city ?? '',
    state: coerceState(org?.location?.state),
    programId: toFiniteNumber(safeItem.externalId) ?? 0,
    programName: safeItem.name ?? '',
    snippet: safeItem.shortDescription ?? '',
    levelOfInstitution: org?.levelOfInstitution ?? '',
    accreditator: isAccreditationHidden(org?.institutionalStats)
      ? ''
      : (org?.accreditator ?? ''),
    percentOnlineEnrollment: coerceNullableNumber(org?.percentOnlineEnrollment),
    isOnline: org?.tags?.isOnline ?? false,
    notForProfit: org?.tags?.isNotForProfit ?? false,
    // Consume the server-resolved `applicationCta` (EDUN-12673) directly.
    // Hard-cut: no `dynamicLinks` fallback — when Offerings does not surface
    // a resolved CTA the URL/label stay empty, which the CTA state machine
    // fails safe to `noop`/`explore` (EDUN-12978 Q1).
    applicationCtaURL: safeItem.applicationCta?.url ?? '',
    applicationCtaLabel: safeItem.applicationCta?.label ?? '',
    // Drawer-detail fields — populated by the offering detail-by-id
    // merge, left empty by the card projection.
    programFlow: [],
    tcpa: '',
  };

  // Apply CTA click-time interpolation config (EDUN-14483): only stamped
  // when Offerings actually resolved a map, so a plain `{ url, label }`
  // CTA leaves these absent and `applicationCtaURL` keeps opening as-is.
  if (isNonEmptyPlainRecord(safeItem.applicationCta?.fieldMap)) {
    result.applicationCtaFieldMap = safeItem.applicationCta?.fieldMap;
  }
  if (isNonEmptyPlainRecord(safeItem.applicationCta?.staticParams)) {
    result.applicationCtaStaticParams = safeItem.applicationCta?.staticParams;
  }
  if (safeItem.applicationCta?.missingValue) {
    result.applicationCtaMissingValue = safeItem.applicationCta.missingValue;
  }

  if (isHttpUrl(safeItem.postLeadCta?.url)) {
    const postLeadCta: NonNullable<Result['postLeadCta']> = {
      url: safeItem.postLeadCta.url,
    };
    if (isNonEmptyString(safeItem.postLeadCta.label)) {
      postLeadCta.label = safeItem.postLeadCta.label;
    }
    if (isNonEmptyPlainRecord(safeItem.postLeadCta.staticParams)) {
      postLeadCta.staticParams = safeItem.postLeadCta.staticParams;
    }
    if (isNonEmptyPlainRecord(safeItem.postLeadCta.fieldMap)) {
      postLeadCta.fieldMap = safeItem.postLeadCta.fieldMap;
    }
    if (
      safeItem.postLeadCta.missingValue === 'empty' ||
      safeItem.postLeadCta.missingValue === 'omit' ||
      safeItem.postLeadCta.missingValue === 'hide'
    ) {
      postLeadCta.missingValue = safeItem.postLeadCta.missingValue;
    }
    result.postLeadCta = postLeadCta;
  }
  if (typeof safeItem.synchronousLeadDelivery === 'boolean') {
    result.synchronousLeadDelivery = safeItem.synchronousLeadDelivery;
  }
  if (typeof org?.showActiveProspectScript === 'boolean') {
    result.showActiveProspectScript = org.showActiveProspectScript;
  }

  if (isNonEmptyString(safeItem.offeringUuid)) {
    result.offeringUuid = safeItem.offeringUuid;
    // CMS v2 `offeringId` is the offerings UUID — there is no distinct
    // CMS-side offering id on the `/match` payload.
    result.offeringId = safeItem.offeringUuid;
  }
  // Guard the indexing with `Array.isArray`: `safeItem` is the
  // `unknown`-tolerant boundary, so a malformed payload could surface
  // `organizationIds` as a string (where `?.[0]` would yield the first
  // character) or another non-array shape. Only read element 0 of a real
  // array so a bad shape leaves `organizationId` absent rather than wrong.
  const organizationId = Array.isArray(safeItem.organizationIds)
    ? safeItem.organizationIds[0]
    : undefined;
  if (isNonEmptyString(organizationId)) {
    result.organizationId = organizationId;
  }
  // Surface the linkout signal from the `linkout`-typed dynamic link. Matched
  // on `type` (no first-link-blind heuristic) so an `application_cta` or other
  // dynamic link is never mistaken for a linkout. The base/unresolved URL is
  // carried; the runtime resolves the conversion-interpolated URL via the BFF
  // 302 at click time.
  const linkoutUrl = Array.isArray(safeItem.dynamicLinks)
    ? safeItem.dynamicLinks.find((link) => link?.type === 'linkout')?.url
    : undefined;
  if (isNonEmptyString(linkoutUrl)) {
    result.linkoutUrl = linkoutUrl;
  }
  const rawAttributes = asPlainRecord(safeItem.attributes);
  if (rawAttributes) {
    result.attributes = { ...rawAttributes };
  }

  // Surface the program-fields pointer from `attributes.programFields`. Only
  // the `flowId` is carried onto `Result`; its presence is the on/off signal
  // for program-specific post-result questions, which the lead-submission hook
  // resolves against dynamic-flows. Stamped only when `flowId` is a non-empty
  // string so a missing/null `attributes` or blank id leaves the pointer absent.
  const programFieldsFlowId = asPlainRecord(
    rawAttributes?.programFields,
  )?.flowId;
  if (isNonEmptyString(programFieldsFlowId)) {
    result.programFields = { flowId: programFieldsFlowId };
  }
  if (isNonEmptyString(org?.images?.logo)) {
    result.imageURL = org?.images?.logo;
  }
  if (typeof org?.tags?.isPublic === 'boolean') {
    result.isPublic = org.tags.isPublic;
  }
  if (typeof org?.tags?.isPrivate === 'boolean') {
    result.isPrivate = org.tags.isPrivate;
  }
  if (typeof org?.tags?.isForProfit === 'boolean') {
    result.forProfit = org.tags.isForProfit;
  }

  applyInstitutionalStats(result, org?.institutionalStats);

  // Merged stat map (in addition to the discrete-field mapping above)
  // so DSL binds can derive display values from one place — e.g.
  // `transform: 'tagLabels'` → EditorialListing pills. The service's
  // `institutionalStats` ships display labels, but the org `tags`
  // booleans and `accreditator` string do not, so the label-less
  // signals are synthesized into the same `{ label, value }` shape
  // here (mirroring legacy `schoolDataPills`, which hardcoded these
  // labels over SchoolDB fields).
  const mergedStats = mergeTagSignalsIntoStats(org);
  if (mergedStats) {
    result.institutionalStats = mergedStats;
  }

  // Stamp the serving endpoint path (`/match` vs `/search`) so it rides each row
  // to event-fire time. Only set when present; absent for non-offerings sources.
  if (isNonEmptyString(resultsSource)) {
    result.resultsSource = resultsSource;
  }

  // List identity: one `sortingReferenceId` per response shared by all its cards
  // (parity with Supersonic). Stays unset when absent rather than fabricating a
  // uuid that would correlate to nothing.
  if (isNonEmptyString(listId)) {
    result.listId = listId;
  }

  return result;
}

function asPlainRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * Map the subset of `institutionalStats` slugs that have a discrete `Result`
 * counterpart consumed by the comparison modal. Each field is set only when
 * the source value is present and coerces cleanly, so the merge stays
 * non-destructive (a missing or malformed stat leaves the field absent, which
 * the modal renders as `N/A`).
 *
 * Three slugs map 1:1 by key/type. Two are reshaped to the legacy `Result`
 * contract: offerings exposes SAT/ACT as a boolean `satOrActRequired`, but the
 * field is a display string; and "free application" (`isFreeApplication`) is
 * the offerings-side expression of a waived online application fee.
 */
function applyInstitutionalStats(
  result: Result,
  stats: OfferingMatchOrg['institutionalStats'],
): void {
  if (!stats) return;

  const retention = toFiniteNumber(statValue(stats, 'fulltimeRetentionRate'));
  if (retention !== undefined) {
    result.fulltimeRetentionRate = retention;
  }

  const onlinePrograms = toFiniteNumber(
    statValue(stats, 'numberOnlinePrograms'),
  );
  if (onlinePrograms !== undefined) {
    result.numberOnlinePrograms = onlinePrograms;
  }

  const transferCredits = toBoolean(
    statValue(stats, 'areTransferCreditsAccepted'),
  );
  if (transferCredits !== undefined) {
    result.areTransferCreditsAccepted = transferCredits;
  }

  const satOrActRequired = toBoolean(statValue(stats, 'satOrActRequired'));
  if (satOrActRequired !== undefined) {
    result.satOrActAdmissionsRequirements = satOrActRequired ? 'Yes' : 'No';
  }

  const freeApplication = toBoolean(statValue(stats, 'isFreeApplication'));
  if (freeApplication !== undefined) {
    result.isApplicationFeeOnlineWaived = freeApplication;
  }
}

/**
 * Build the `Result.institutionalStats` map: the org's boolean tag
 * signals synthesized into labeled `{ label, value }` entries, followed
 * by the service's `institutionalStats` verbatim. Synthesized keys:
 *
 *   - `isOnline`            ← `org.tags.isOnline`        → "Online"
 *   - `isAccredited`        ← `org.accreditator` non-empty → "Accredited"
 *   - `isPrivate`           ← `org.tags.isPrivate`       → "Private"
 *   - `isNotForProfit`      ← `org.tags.isNotForProfit`  → "Not-for-Profit"
 *   - `satOrActNotRequired` ← `institutionalStats.satOrActRequired === false`
 *                             → "No SAT/ACT Required"
 *
 * Only affirmative signals are synthesized (a `false`/absent flag adds
 * no entry), synthesized entries come FIRST so the uncurated
 * `tagLabels` output leads with the legacy-style pills, and the
 * service's stats win on key collision (its labels are authoritative).
 * Returns `undefined` when there is nothing to expose so sparse orgs
 * leave the field absent.
 *
 * `satOrActNotRequired` is a positive-polarity companion to the service's
 * `satOrActRequired` boolean: the `tagLabels` transform is affirmative-only
 * (it renders a pill only when a value is truthy), so a "not required"
 * signal — which the service ships as `satOrActRequired: false` — can never
 * surface a pill on its own. Synthesizing an inverted `true` entry lets the
 * EL render the "No SAT/ACT Required" badge without changing the transform.
 * The key is new (not present in the service map), so the verbatim spread
 * below never clobbers it, and the original `satOrActRequired: false` still
 * flows through (harmlessly dropped by the affirmative-only transform).
 */
function mergeTagSignalsIntoStats(
  org: OfferingMatchOrg | undefined,
): NonNullable<OfferingMatchOrg['institutionalStats']> | undefined {
  const synthesized: NonNullable<OfferingMatchOrg['institutionalStats']> = {};
  if (org?.tags?.isOnline === true) {
    synthesized.isOnline = { label: 'Online', value: true };
  }
  if (
    isNonEmptyString(org?.accreditator) &&
    !isAccreditationHidden(org?.institutionalStats)
  ) {
    synthesized.isAccredited = { label: 'Accredited', value: true };
  }
  if (org?.tags?.isPrivate === true) {
    synthesized.isPrivate = { label: 'Private', value: true };
  }
  if (org?.tags?.isNotForProfit === true) {
    synthesized.isNotForProfit = { label: 'Not-for-Profit', value: true };
  }
  if (
    org?.institutionalStats &&
    toBoolean(statValue(org.institutionalStats, 'satOrActRequired')) === false
  ) {
    synthesized.satOrActNotRequired = {
      label: 'No SAT/ACT Required',
      value: true,
    };
  }

  const merged = { ...synthesized, ...org?.institutionalStats };
  return Object.keys(merged).length > 0 ? merged : undefined;
}

function statValue(
  stats: NonNullable<OfferingMatchOrg['institutionalStats']>,
  key: string,
): unknown {
  const entry = stats[key];
  return entry && typeof entry === 'object' ? entry.value : undefined;
}

function toBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return undefined;
}

function coerceState(state: OfferingMatchState | undefined): string {
  if (typeof state === 'string') {
    return state;
  }
  if (state && typeof state === 'object') {
    return state.abbreviation ?? state.name ?? '';
  }
  return '';
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function coerceNullableNumber(value: unknown): number | null {
  const parsed = toFiniteNumber(value);
  return parsed === undefined ? null : parsed;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isHttpUrl(value: unknown): value is string {
  if (!isNonEmptyString(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isNonEmptyPlainRecord(
  value: unknown,
): value is Record<string, string> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  return (
    entries.length > 0 &&
    entries.every(([, entryValue]) => typeof entryValue === 'string')
  );
}
